import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { fadeUp, stagger, viewport } from "@/lib/motion";
import { pillars } from "@/lib/content";

const PageHero = ({ overline, title, sub }) => (
  <section className="pt-36 md:pt-48 pb-16 md:pb-24">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <motion.div initial="hidden" animate="visible" variants={stagger}>
        <motion.div variants={fadeUp} className="text-xs uppercase tracking-[0.25em] text-[#0B1F3A]/60 mb-6">{overline}</motion.div>
        <motion.h1 variants={fadeUp} className="text-5xl md:text-7xl lg:text-8xl text-[#0B1F3A] tracking-tight leading-[0.98] max-w-4xl">{title}</motion.h1>
        {sub && <motion.p variants={fadeUp} className="mt-8 text-lg md:text-xl text-gray-500 max-w-2xl leading-relaxed">{sub}</motion.p>}
      </motion.div>
    </div>
  </section>
);

export default function Services() {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="services-page">
      <PageHero
        overline="Nuestros servicios"
        title="Soluciones a tus retos, no servicios sueltos."
        sub="Cuatro áreas que trabajan como un solo equipo. En cada una partimos de tu reto real, te explicamos cómo te ayudamos y el impacto que puedes esperar."
      />

      <section className="pb-10">
        {pillars.map((p, i) => {
          const Icon = p.icon;
          const reverse = i % 2 === 1;
          return (
            <div key={p.id} data-testid={`service-${p.id}`} className={`${i % 2 === 1 ? "bg-[#F3F4F6]" : "bg-white"}`}>
              <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                  <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={viewport}
                    className={`lg:col-span-6 ${reverse ? "lg:order-2 lg:col-start-7" : ""}`}>
                    <motion.div variants={fadeUp} className="w-14 h-14 rounded-2xl bg-[#0B1F3A] flex items-center justify-center mb-8">
                      <Icon size={24} className="text-white" />
                    </motion.div>
                    <motion.h2 variants={fadeUp} className="text-4xl md:text-5xl font-serif text-[#0B1F3A] tracking-tight">{p.title}</motion.h2>
                    <motion.div variants={fadeUp} className="mt-10 space-y-8">
                      {[["El reto empresarial", p.problem], ["Cómo te ayudamos", p.solution], ["Impacto esperado", p.impact]].map(([label, body], j) => (
                        <div key={j} className="border-l-2 border-[#0B1F3A]/15 pl-6">
                          <div className="text-xs uppercase tracking-[0.2em] text-[#0B1F3A]/60 mb-2">{label}</div>
                          <p className="text-gray-700 leading-relaxed max-w-lg">{body}</p>
                        </div>
                      ))}
                    </motion.div>
                  </motion.div>

                  <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewport}
                    className={`lg:col-span-6 ${reverse ? "lg:order-1" : "lg:col-start-7"}`}>
                    <div className="rounded-[2rem] overflow-hidden h-[380px] md:h-[520px]">
                      <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          );
        })}
      </section>

      <section className="bg-[#0B1F3A] text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 text-center">
          <h2 className="text-4xl md:text-6xl font-serif tracking-tight max-w-3xl mx-auto leading-[1.05]">¿No sabes por dónde empezar? Pide un diagnóstico inicial.</h2>
          <Link to="/contact" data-testid="services-cta" className="mt-10 inline-flex items-center gap-2 rounded-full bg-white text-[#0B1F3A] px-8 py-4 font-medium hover:scale-[1.03] transition-transform">
            Solicitar un diagnóstico inicial <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </motion.div>
  );
}
